/**
 * EditPositionModal — Açık pozisyon üzerinde TP/SL/Leverage düzenleme.
 *
 * Backend endpoint'leri var (/api/exchange-ops/hedge-tpsl, /leverage),
 * UI tarafı eksikti. Artık her bacağa ayrı TP/SL set edilebilir;
 * kaldıraç değiştirme her iki bacakta paralel uygulanır.
 *
 * NOT: TP/SL price-based değil pct-based gönderiliyor (backend bekleyiş).
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

export default function EditPositionModal({ position, mode, onClose }) {
  const qc = useQueryClient();
  const isLev = mode === "leverage";
  const isTpsl = mode === "tpsl";

  const [leverage, setLeverage] = useState(String(position?.leverage || 5));
  const [tpPct, setTpPct] = useState("2.0");
  const [slPct, setSlPct] = useState("4.0");

  const levMut = useMutation({
    mutationFn: () =>
      api.exLeverage({
        symbol: position.symbol,
        leverage: parseInt(leverage, 10),
      }),
    onSuccess: () => {
      toast.success(`Kaldıraç ${leverage}x olarak güncellendi`);
      qc.invalidateQueries({ queryKey: ["positions"] });
      onClose();
    },
    onError: (e) => toast.error(errMsg(e, "Kaldıraç değiştirilemedi")),
  });

  const tpslMut = useMutation({
    mutationFn: () => {
      const entry = position.entry_price_darkex || position.entry_price_counter;
      const tpAbs = entry * (1 + parseFloat(tpPct) / 100);
      const slAbs = entry * (1 - parseFloat(slPct) / 100);
      return api.exHedgeTpsl({
        symbol: position.symbol,
        side: position.darkex_side, // ana yön Darkex
        tp_price: tpAbs,
        sl_price: slAbs,
      });
    },
    onSuccess: () => {
      toast.success(`TP/SL güncellendi (TP %${tpPct} · SL %${slPct})`);
      qc.invalidateQueries({ queryKey: ["positions"] });
      onClose();
    },
    onError: (e) => toast.error(errMsg(e, "TP/SL güncellenemedi")),
  });

  if (!position) return null;

  const handleSubmit = () => {
    if (isLev) levMut.mutate();
    else if (isTpsl) tpslMut.mutate();
  };

  const pending = levMut.isPending || tpslMut.isPending;

  return (
    <div
      data-testid="edit-position-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)",
        zIndex: 100, display: "flex", alignItems: "center",
        justifyContent: "center", padding: 20,
      }}
    >
      <div
        style={{
          background: "#0a0a0b",
          border: "1px solid rgba(255,255,255,0.08)",
          maxWidth: 420, width: "100%", padding: 24,
        }}
      >
        <div style={{
          fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase",
          color: "#ff5d15", marginBottom: 10,
        }}>
          {isLev ? "Kaldıraç Değiştir" : "TP / SL Düzenle"}
        </div>
        <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 14 }}>
          {position.symbol} · {position.darkex_side} (Darkex) / {position.counter_side} ({position.counter_exchange})
        </h3>

        {isLev && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 11, color: "#9ca3af",
                            marginBottom: 6 }}>
              Yeni Kaldıraç (1-125)
            </label>
            <input
              type="number"
              min="1" max="125"
              value={leverage}
              onChange={(e) => setLeverage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              data-testid="edit-leverage-input"
              autoFocus
              style={{
                width: "100%", height: 38, background: "transparent",
                border: "1px solid rgba(255,255,255,0.1)", color: "#fff",
                padding: "0 12px", fontSize: 14,
                fontFamily: "ui-monospace, monospace",
              }}
            />
            <p style={{ fontSize: 11, color: "#6b7280", marginTop: 8 }}>
              Mevcut: {position.leverage}x · Notional={(position.size_eth * (position.entry_price_darkex || 0)).toFixed(0)}$
            </p>
          </div>
        )}

        {isTpsl && (
          <div style={{ marginBottom: 16, display: "grid",
                        gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ display: "block", fontSize: 11, color: "#4ade80",
                              marginBottom: 6 }}>
                Take Profit (%)
              </label>
              <input
                type="number" step="0.1" min="0.1" max="50"
                value={tpPct}
                onChange={(e) => setTpPct(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                data-testid="edit-tp-input"
                autoFocus
                style={{
                  width: "100%", height: 38, background: "transparent",
                  border: "1px solid rgba(74,222,128,0.3)", color: "#4ade80",
                  padding: "0 12px", fontSize: 14,
                  fontFamily: "ui-monospace, monospace",
                }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, color: "#fb7185",
                              marginBottom: 6 }}>
                Stop Loss (%)
              </label>
              <input
                type="number" step="0.1" min="0.1" max="50"
                value={slPct}
                onChange={(e) => setSlPct(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                data-testid="edit-sl-input"
                style={{
                  width: "100%", height: 38, background: "transparent",
                  border: "1px solid rgba(251,113,133,0.3)", color: "#fb7185",
                  padding: "0 12px", fontSize: 14,
                  fontFamily: "ui-monospace, monospace",
                }}
              />
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={pending}
            data-testid="edit-position-submit"
            style={{
              flex: 1, height: 38,
              background: pending ? "rgba(255,255,255,0.05)" : "#ff5d15",
              color: pending ? "#6b7280" : "#0a0a0b",
              border: 0, fontWeight: 600, fontSize: 12,
              letterSpacing: "0.15em", textTransform: "uppercase",
              cursor: pending ? "not-allowed" : "pointer",
            }}
          >
            {pending ? "Gönderiliyor..." : "Uygula (Enter)"}
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="edit-position-cancel"
            style={{
              flex: 1, height: 38, background: "transparent",
              color: "#9ca3af", border: "1px solid rgba(255,255,255,0.1)",
              fontSize: 12, letterSpacing: "0.15em",
              textTransform: "uppercase", cursor: "pointer",
            }}
          >
            İptal (Esc)
          </button>
        </div>
      </div>
    </div>
  );
}
